export const TRIAL_POLICY_VERSION = "trial-policy.2026-09.v1";

export const TRIAL_DAYS = 14;
export const TRIAL_ENDING_SOON_DAYS = 3;
export const TRIAL_REMINDER_DAYS = [7, 3, 1, 0] as const;

export type TrialLifecycleState =
  | "ACTIVE"
  | "ENDING_SOON"
  | "EXPIRED"
  | "CONVERTED"
  | "NONE";

export type TrialReminderMilestone =
  | "DAY_7_VALUE"
  | "ENDING_3_DAYS"
  | "ENDING_1_DAY"
  | "ENDED";

const DAY_MS = 24 * 60 * 60 * 1000;

export function trialEndFromStart(start: Date) {
  return new Date(start.getTime() + TRIAL_DAYS * DAY_MS);
}

export function resolveTrialLifecycle(
  input: {
    planCode: string | null;
    source: string | null;
    status: string | null;
    trialStart: Date | null;
    trialEnd: Date | null;
  },
  now = new Date(),
): TrialLifecycleState {
  const hasTrialHistory = Boolean(input.trialStart || input.trialEnd);

  if (
    hasTrialHistory
    && input.planCode
    && input.planCode !== "TRIAL"
    && input.source
    && input.source !== "TRIAL"
  ) {
    return "CONVERTED";
  }

  const isTrial =
    input.planCode === "TRIAL"
    || input.source === "TRIAL"
    || input.status === "TRIALING";

  if (!isTrial) {
    return "NONE";
  }

  if (!input.trialEnd) {
    return "EXPIRED";
  }

  if (input.status === "EXPIRED" || input.trialEnd.getTime() <= now.getTime()) {
    return "EXPIRED";
  }

  const remainingMs = input.trialEnd.getTime() - now.getTime();

  if (remainingMs <= TRIAL_ENDING_SOON_DAYS * DAY_MS) {
    return "ENDING_SOON";
  }

  return "ACTIVE";
}


export function resolveTrialReminderMilestone(
  input: {
    planCode: string | null;
    source: string | null;
    status: string | null;
    trialStart: Date | null;
    trialEnd: Date | null;
  },
  now = new Date(),
): TrialReminderMilestone | null {
  const lifecycle = resolveTrialLifecycle(input, now);

  if (lifecycle === "NONE" || lifecycle === "CONVERTED") {
    return null;
  }

  if (!input.trialStart || !input.trialEnd) {
    return null;
  }

  if (lifecycle === "EXPIRED") {
    return "ENDED";
  }

  const remainingMs = input.trialEnd.getTime() - now.getTime();

  if (remainingMs <= DAY_MS) {
    return "ENDING_1_DAY";
  }

  if (remainingMs <= TRIAL_ENDING_SOON_DAYS * DAY_MS) {
    return "ENDING_3_DAYS";
  }

  const elapsedMs = now.getTime() - input.trialStart.getTime();

  if (elapsedMs >= 7 * DAY_MS) {
    return "DAY_7_VALUE";
  }

  return null;
}
